import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useGlobalProvider } from "../context/GlobalContext";
import {
  deleteDealAssignee,
  getDealAssignees,
  getNotifications,
  getUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
  markNotificationsReadByEntity,
} from "../services/notificationsApi";
import type { DealAssignee, Notification, ReadByEntityBody } from "../types/dealTask";
import { SocketProvider } from "./SocketContext";

const POLL_INTERVAL_MS = 60_000;

interface NotificationsContextValue {
  unreadCount: number;
  unreadLoading: boolean;
  notifications: Notification[];
  candidateNotifications: Notification[];
  inboxLoading: boolean;
  refetchUnreadCount: () => Promise<void>;
  fetchInbox: (unreadOnly?: boolean) => Promise<void>;
  markRead: (notificationId: number) => Promise<boolean>;
  markAllRead: () => Promise<boolean>;
  markReadByEntity: (body: ReadByEntityBody) => Promise<boolean>;
}

const NotificationsContext = createContext<
  NotificationsContextValue | undefined
>(undefined);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { user, userLoading } = useGlobalProvider();
  const userId = user?.tenfiftyid as number | undefined;

  // Server-backed unread count (from the unread-count endpoint). Local
  // notifications (negative ids, e.g. real-time deal updates and deal
  // assignments) are not known to the server, so they are counted separately
  // and the two are summed; a poll therefore never drops local unread items.
  const [serverUnreadCount, setServerUnreadCount] = useState(0);
  const [unreadLoading, setUnreadLoading] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [candidateNotifications, setCandidateNotifications] = useState<
    Notification[]
  >([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const assigneesFetchedForUserRef = useRef<number | null>(null);

  const refetchUnreadCount = useCallback(async () => {
    if (!userId) return;
    setUnreadLoading(true);
    try {
      const response = await getUnreadCount(userId);
      if (response?.success && response.data) {
        setServerUnreadCount(response.data.unread_count);
      }
    } catch {
      // keep last known count
    } finally {
      setUnreadLoading(false);
    }
  }, [userId]);

  const fetchInbox = useCallback(
    async (unreadOnly = true) => {
      if (!userId) return;
      setInboxLoading(true);
      try {
        const response = await getNotifications(userId, unreadOnly);
        if (response?.success && response.data) {
          const inboxNotifications = response.data;

          setNotifications((prev) => {
            const localNotifications = prev.filter(
              (notification) =>
                notification.id < 0 &&
                (notification.type === "deal_updated" ||
                  notification.type === "deal_assigned"),
            );

            return [...localNotifications, ...inboxNotifications];
          });
        }
      } catch {
        // keep last inbox
      } finally {
        setInboxLoading(false);
      }
    },
    [userId],
  );

  const markRead = useCallback(
    async (notificationId: number) => {
      if (notificationId < 0) {
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        setCandidateNotifications((prev) =>
          prev.filter((n) => n.id !== notificationId),
        );
        return true;
      }

      if (!userId) return false;
      const response = await markNotificationRead(notificationId, userId);
      if (response?.success) {
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        await refetchUnreadCount();
        return true;
      }
      return false;
    },
    [userId, refetchUnreadCount],
  );

  const markAllRead = useCallback(async () => {
    if (!userId) return false;

    await deleteCandidateAssignees(candidateNotifications);

    const response = await markAllNotificationsRead(userId);
    if (response?.success) {
      setNotifications([]);
      setCandidateNotifications([]);
      setServerUnreadCount(0);
      return true;
    }
    return false;
  }, [userId, candidateNotifications]);

  const markReadByEntity = useCallback(
    async (body: ReadByEntityBody) => {
      if (!userId) return false;
      const response = await markNotificationsReadByEntity(userId, body);
      if (response?.success) {
        await refetchUnreadCount();
        return true;
      }
      return false;
    },
    [userId, refetchUnreadCount],
  );

  useEffect(() => {
    if (userLoading || !userId) return;
    void refetchUnreadCount();
  }, [userLoading, userId, refetchUnreadCount]);

  useEffect(() => {
    if (userLoading || !userId) return;

    const displayName = user?.displayName?.trim();
    if (!displayName) return;

    if (assigneesFetchedForUserRef.current === userId) return;
    assigneesFetchedForUserRef.current = userId;

    void (async () => {
      try {
        const response = await getDealAssignees();
        if (!response?.success || !Array.isArray(response.data)) return;

        const builtNotifications = buildAssigneeNotifications(
          response.data,
          displayName,
        );

        if (builtNotifications.length === 0) return;

        setCandidateNotifications(builtNotifications);
        setNotifications((prev) => {
          const existingKeys = new Set(
            prev
              .map(getDealAssignedKey)
              .filter((key): key is string => key !== null),
          );

          const toAdd = builtNotifications.filter((notification) => {
            const key = getDealAssignedKey(notification);
            return key !== null && !existingKeys.has(key);
          });

          if (toAdd.length === 0) return prev;

          return [...toAdd, ...prev];
        });
      } catch (error) {
        console.error("deal-assignees", error);
      }
    })();
  }, [userLoading, userId, user?.displayName]);

  useEffect(() => {
    if (userLoading || !userId) return;

    const tick = () => {
      if (document.visibilityState === "visible") {
        void refetchUnreadCount();
      }
    };

    intervalRef.current = setInterval(tick, POLL_INTERVAL_MS);

    const onFocus = () => void refetchUnreadCount();
    window.addEventListener("focus", onFocus);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener("focus", onFocus);
    };
  }, [userLoading, userId, refetchUnreadCount]);

  const localUnreadCount = useMemo(
    () => notifications.filter(isUnreadLocalNotification).length,
    [notifications],
  );

  const unreadCount = serverUnreadCount + localUnreadCount;

  return (
    <NotificationsContext.Provider
      value={{
        unreadCount,
        unreadLoading,
        notifications,
        candidateNotifications,
        inboxLoading,
        refetchUnreadCount,
        fetchInbox,
        markRead,
        markAllRead,
        markReadByEntity,
      }}
    >
      <SocketProvider
        notifications={notifications}
        setNotifications={setNotifications}
      >
        {children}
      </SocketProvider>
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationsProvider",
    );
  }
  return context;
}

let localAssigneeNotificationId = 0;

function isUnreadLocalNotification(notification: Notification): boolean {
  return notification.id < 0 && !notification.is_read;
}

function getDealAssignedKey(notification: Notification): string | null {
  if (notification.type !== "deal_assigned") return null;
  return `${notification.payload.deal_id}:${notification.payload.role}`;
}

async function deleteCandidateAssignees(
  candidates: Notification[],
): Promise<void> {
  const dealIds = new Set<number>();

  for (const notification of candidates) {
    if (notification.type !== "deal_assigned") continue;
    dealIds.add(notification.payload.deal_id);
  }

  for (const dealId of dealIds) {
    try {
      await deleteDealAssignee(dealId);
    } catch (error) {
      console.error("delete deal-assignee", dealId, error);
    }
  }
}

function buildAssigneeNotifications(
  assignees: DealAssignee[],
  displayName: string,
): Notification[] {
  const notifications: Notification[] = [];

  for (const item of assignees) {
    if (item.deal_lead?.name === displayName) {
      notifications.push(
        buildDealAssignedNotification(item, "deal_lead"),
      );
    }

    if (item.deal_support?.name === displayName) {
      notifications.push(
        buildDealAssignedNotification(item, "deal_support"),
      );
    }
  }

  return notifications;
}

function buildDealAssignedNotification(
  assignee: DealAssignee,
  role: "deal_lead" | "deal_support",
): Notification {
  localAssigneeNotificationId -= 1;

  const roleLabel = role === "deal_lead" ? "deal lead" : "deal support";
  const byClause = assignee.update_by ? ` by ${assignee.update_by}` : "";

  return {
    id: localAssigneeNotificationId,
    user_id: 0,
    type: "deal_assigned",
    entity_type: "deal",
    entity_id: assignee.deal_id,
    title: "Deal assignment",
    message: `You have been assigned as ${roleLabel} to ${assignee.deal_name}${byClause}`,
    payload: {
      deal_id: assignee.deal_id,
      deal_name: assignee.deal_name,
      role,
      assigned_by: assignee.update_by ?? "",
    },
    read_at: null,
    created_at: new Date().toISOString(),
    is_read: false,
  };
}
